import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useRef, useState } from 'react';
import useOutsideHoverElement from 'components/Dashboard/hooks/useOutsideHoverElement';
import useSearchBarUiFunctions from 'components/Dashboard/hooks/useSearchBarUiFunctions';
import AddTopicButton from 'components/Dashboard/Navigation/AddTopicButton';
import TopicItem from './TopicItem';
import TopicListNoAuthenticatedUsers from './TopicListNoAuthenticatedUsers';
import { useLocation } from 'react-router-dom';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import useRecentSearchesTopicPicker from 'components/Common/TopicPicker/useRecentSearchesTopicPicker';
import { RecentSearchItem } from 'components/Dashboard/Navigation/TopNavSearch';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';

const TopicPickerList = (props) => {
  const location = useLocation();
  const {
    keywordsLists = [],
    applySearchQueryOrTopic,
    inputSearch,
    showDropdownContent,
    topic,
    searchValue,
    showTopicSelected,
    dataTooltip,
    showActions,
    setLocalURLState,
    recentItemsCondition,
    setSearchValue,
    limitToCountOfItems,
    selectedTopic,
    alwaysShowSelectedMark,
    showMasterTopicIcon,
  } = props;
  const widget = location.pathname.startsWith('/embed-analytics');
  const { isAuthenticated } = useAuth0();

  const keywordListsElement = useRef(null);
  const scrollContainerElement = useRef(null);
  const topicsListElement = useRef(null);

  const initialMaxHeight = limitToCountOfItems ? limitToCountOfItems * 60 : 260;
  const [maxHeight, setMaxHeight] = useState(initialMaxHeight);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);

  const { startMoveLists, listPosition, setStartMoveLists, setListPosition, scrollList } = useSearchBarUiFunctions({
    inputSearch,
    keywordListsElement,
    scrollContainerElement,
    topicsListElement,
    showDropdownContent,
  });

  const { recentSearches, setRecentSearches, getRecentSearchesAndKeywordsLists, measureHeightForFirstNItems } =
    useRecentSearchesTopicPicker();
  const keywordsListsToUse = recentItemsCondition
    ? getRecentSearchesAndKeywordsLists(keywordsLists)?.topics
    : keywordsLists;
  const recentSearchesToUse = recentItemsCondition ? getRecentSearchesAndKeywordsLists(keywordsLists)?.searches : [];

  const { newAgencyPlan } = useTopicOrClientWord();

  const unselectTopic = () => {
    setStartMoveLists(false);
  };
  useOutsideHoverElement(topicsListElement, unselectTopic);
  const agencyPlanCondition = newAgencyPlan && keywordsLists?.length > 0;

  useEffect(() => {
    const updatePositionStyles = () => {
      if (scrollContainerElement?.current) {
        if (limitToCountOfItems && keywordsListsToUse?.length > 0 && keywordListsElement?.current) {
          // Measure actual height of first N items
          const totalHeight = measureHeightForFirstNItems({
            items: keywordListsElement.current.children,
            limit: limitToCountOfItems,
          });
          if (totalHeight > 0) {
            setMaxHeight(totalHeight);
          }
        } else {
          const { top } = scrollContainerElement.current.getBoundingClientRect();
          const availableHeight = window.innerHeight - (top + 50);
          setMaxHeight(Math.min(initialMaxHeight, availableHeight - 40));
        }
      }
    };
    updatePositionStyles();
    window.addEventListener('resize', updatePositionStyles);

    return () => {
      window.removeEventListener('resize', updatePositionStyles);
    };
  }, [showDropdownContent, keywordsListsToUse, limitToCountOfItems]);

  useEffect(() => {
    if (!recentItemsCondition || !showDropdownContent || !scrollContainerElement?.current) return;
    scrollContainerElement.current.scrollTop = 0;
    setIsScrolledToBottom(false);
  }, [showDropdownContent, recentItemsCondition]);

  useEffect(() => {
    if (!recentItemsCondition) return;
    const el = scrollContainerElement?.current;
    if (!el || !showDropdownContent) return;
    const checkScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      setIsScrolledToBottom(scrollTop + clientHeight >= scrollHeight - 1);
    };
    checkScroll();
    el.addEventListener('scroll', checkScroll);
    return () => el.removeEventListener('scroll', checkScroll);
  }, [showDropdownContent, keywordsListsToUse, recentItemsCondition]);

  const shouldShowFade =
    recentItemsCondition &&
    limitToCountOfItems != null &&
    keywordsListsToUse?.length > limitToCountOfItems &&
    !isScrolledToBottom;

  return (
    <>
      {(isAuthenticated || widget) && (
        <div
          ref={topicsListElement}
          onKeyDown={(e) => {
            scrollList(e);
          }}
        >
          <div
            className={`scroll-container hover-container search-bar-lists ${
              recentItemsCondition ? 'search-bar-lists-recent-items' : ''
            }`}
            style={{ maxHeight: `${maxHeight}px` }}
            ref={scrollContainerElement}
          >
            <ul className='policy-list px-0 mb-0 hover-content' ref={keywordListsElement}>
              {keywordsListsToUse.map((item, index) => (
                <TopicItem
                  key={`keywordlist${item?.id === null ? 'master-topic' : item.id}`}
                  item={item}
                  index={index}
                  applyTopic={applySearchQueryOrTopic}
                  listPosition={listPosition}
                  setListPosition={setListPosition}
                  startMoveLists={startMoveLists}
                  setStartMoveLists={setStartMoveLists}
                  topic={topic}
                  selectedTopic={selectedTopic}
                  searchValue={searchValue}
                  showTopicSelected={showTopicSelected}
                  showActions={showActions}
                  setLocalURLState={setLocalURLState}
                  alwaysShowSelectedMark={alwaysShowSelectedMark}
                  showMasterTopicIcon={showMasterTopicIcon}
                />
              ))}
            </ul>
            {shouldShowFade && <div className='search-bar-lists-recent-items-fade' />}
          </div>
          {recentItemsCondition && (
            <ul className='policy-list px-0 mb-0 hover-content'>
              {recentSearchesToUse.map((item, index) => (
                <RecentSearchItem
                  key={`recentSearch${index}`}
                  item={item}
                  searchResults={(value) => {
                    setSearchValue(value);
                    applySearchQueryOrTopic({ freeSearch: value });
                  }}
                  recentSearches={recentSearches}
                  setRecentSearches={setRecentSearches}
                  index={index}
                  selected={getUrlParam('search') === item.value}
                />
              ))}
            </ul>
          )}
          {!widget && !agencyPlanCondition && (
            <AddTopicButton keywordsLists={keywordsLists} dataTooltip={dataTooltip} />
          )}
        </div>
      )}
      {!widget && <TopicListNoAuthenticatedUsers />}
    </>
  );
};

export default TopicPickerList;
